import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, FileText, Info, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CodeBlock, DocSection } from "./DocComponents";

/**
 * Postback URL Tab — pub configures a server-to-server URL with macros.
 * Renders the input, live preview with sample values, available-macros
 * picker, and inline documentation.
 */
export function PostbackUrlTab({
  postbackUrl,
  setPostbackUrl,
  savingPb,
  macros,
  subId,
  onSave,
}) {
  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  // Sample-value substitution preview. We replace each {macro} with a
  // realistic stub so the publisher can sanity-check their URL shape
  // before saving. Empty values map to empty (so the URL doesn't show
  // literal "{sub_id2}" if they don't actually use it).
  const preview = postbackUrl
    .replace("{sub_id1}", subId)
    .replace("{sub_id2}", "")
    .replace("{sub_id3}", "")
    .replace("{shared_id}", "")
    .replace("{click_id}", "abc123")
    .replace("{action_id}", "1733.6628.1547748")
    .replace("{sale_amount}", "65.98")
    .replace("{commission}", "4.62")
    .replace("{commission_pct}", "7")
    .replace("{status}", "APPROVED")
    .replace("{brand}", "MacPaw")
    .replace("{campaign_id}", "1733")
    .replace("{order_id}", "ORD-98765")
    .replace("{currency}", "USD")
    .replace("{event_date}", "2026-04-17")
    .replace("{country}", "US")
    .replace("{promo_code}", "");

  return (
    <div className="space-y-6">
      <div className="border border-blue-200 bg-blue-50/50 rounded-md p-4 flex gap-3">
        <Info size={18} className="text-[#002FA7] shrink-0 mt-0.5" />
        <div className="text-sm text-slate-700 leading-relaxed">
          <p className="font-medium text-slate-900 mb-1">Server-to-Server Postback</p>
          <p>When a conversion happens, our system forwards the data to your postback URL in real-time with your commission rates applied. Use macros to map data into your URL.</p>
          <p className="mt-2 text-xs text-slate-500">Your SubID: <span className="font-mono font-bold text-[#002FA7]">{subId}</span></p>
        </div>
      </div>

      {/* Postback URL Input */}
      <div className="border border-slate-200 rounded-md p-5">
        <h3 className="font-heading text-sm font-semibold text-slate-900 mb-4">Your Postback URL</h3>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-500 font-bold">URL with Macros</Label>
            <Input value={postbackUrl} onChange={e => setPostbackUrl(e.target.value)} placeholder="https://your-server.com/postback?subid={sub_id1}&amount={sale_amount}&commission={commission}&status={status}" className="h-10 font-mono text-xs" data-testid="postback-url-input" />
          </div>
          <div className="flex gap-2">
            <Button onClick={onSave} disabled={savingPb} className="bg-[#002FA7] hover:bg-[#00227A] text-white" data-testid="save-postback-btn">
              {savingPb ? "Saving..." : "Save Postback URL"}
            </Button>
            {postbackUrl && <Button variant="outline" onClick={() => setPostbackUrl("")}>Clear</Button>}
          </div>
        </div>
      </div>

      {/* Preview */}
      {postbackUrl && (
        <div className="border border-slate-200 rounded-md p-5">
          <h3 className="font-heading text-sm font-semibold text-slate-900 mb-3">Preview (sample values)</h3>
          <div className="bg-slate-50 border border-slate-200 rounded px-3 py-2 font-mono text-xs text-slate-600 break-all">
            {preview}
          </div>
        </div>
      )}

      {/* Available Macros */}
      <div className="border border-slate-200 rounded-md p-5">
        <h3 className="font-heading text-sm font-semibold text-slate-900 mb-4">Available Macros</h3>
        <p className="text-xs text-slate-500 mb-3">Click "Insert" to add a macro to your URL, or copy it.</p>
        <div className="space-y-1">
          {macros.map(m => (
            <div key={m.macro} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0 hover:bg-slate-50 px-2 rounded transition-colors">
              <div className="flex items-center gap-3">
                <code className="font-mono text-xs text-[#002FA7] font-bold bg-[#002FA7]/5 px-2 py-1 rounded">{m.macro}</code>
                <span className="text-sm text-slate-600">{m.description}</span>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setPostbackUrl(prev => prev + m.macro); toast.success(`Added ${m.macro}`); }}>Insert</Button>
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => copyText(m.macro)}><Copy size={12} /></Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Postback Documentation */}
      <div className="border border-slate-200 rounded-md p-5">
        <div className="flex items-center gap-2 mb-4">
          <FileText size={18} className="text-[#002FA7]" />
          <h3 className="font-heading text-sm font-semibold text-slate-900">Postback Documentation</h3>
        </div>
        <DocSection title="How It Works">
          <ol className="text-xs text-slate-600 space-y-2 list-decimal ml-4">
            <li>User clicks your tracking link (with your SubID appended)</li>
            <li>User makes a purchase on the brand's website</li>
            <li>The affiliate network detects the conversion and fires a postback to Engagino</li>
            <li>Engagino calculates your commission at your custom rate</li>
            <li>Engagino fires a GET request to your postback URL with macros replaced</li>
          </ol>
        </DocSection>
        <DocSection title="Example Postback URL">
          <CodeBlock>{`https://your-server.com/conversion?sub_id={sub_id1}&sale={sale_amount}&commission={commission}&status={status}&brand={brand}&order={order_id}`}</CodeBlock>
        </DocSection>
        <DocSection title="What Your Server Receives">
          <CodeBlock>{`GET https://your-server.com/conversion?sub_id=${subId}&sale=65.98&commission=4.62&status=APPROVED&brand=Best%20Buy%20U.S&order=ORD-98765`}</CodeBlock>
          <p className="text-xs text-slate-500 mt-2">Your server should return HTTP 200 to acknowledge receipt.</p>
        </DocSection>
        <div className="border border-amber-200 bg-amber-50/50 rounded-md p-3 flex gap-2">
          <Warning size={16} className="text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs text-slate-700"><strong>Important:</strong> Your postback endpoint must respond within 15 seconds. Failed postbacks are logged but not retried automatically.</p>
        </div>
      </div>
    </div>
  );
}
